import { useState } from "react";
import { Sparkles, Loader2, Check, CalendarPlus, RotateCw } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { WHEEL_MONTHS } from "./shared";

export function AiAnnualPlanDialog({ companyId, year, onApplied }) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);
  const [plan, setPlan] = useState(null);
  const [wheelSel, setWheelSel] = useState({});
  const [meetSel, setMeetSel] = useState({});

  const generate = async () => {
    setLoading(true);
    setPlan(null);
    try {
      const { data } = await api.post(`/companies/${companyId}/ai/annual-plan`, { year });
      setPlan(data);
      setWheelSel(Object.fromEntries((data.wheel_points || []).map((_, i) => [i, true])));
      setMeetSel(Object.fromEntries((data.meetings || []).map((_, i) => [i, true])));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      setOpen(false);
    } finally {
      setLoading(false);
    }
  };

  const openDialog = () => {
    setOpen(true);
    generate();
  };

  const apply = async () => {
    const wheel_points = (plan.wheel_points || []).filter((_, i) => wheelSel[i]);
    const meetings = (plan.meetings || []).filter((_, i) => meetSel[i]);
    if (!wheel_points.length && !meetings.length) {
      toast.error("Velg minst ett punkt eller møte");
      return;
    }
    setApplying(true);
    try {
      const { data } = await api.post(`/companies/${companyId}/ai/annual-plan/apply`, { wheel_points, meetings });
      toast.success(`La til ${data.added_points} årshjul-punkt(er) og ${data.created_meetings} møte(r)`);
      setOpen(false);
      setPlan(null);
      onApplied?.();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setApplying(false);
    }
  };

  const typeLabel = (t) => (t === "generalforsamling" ? "Generalforsamling" : "Styremøte");

  return (
    <>
      <Button size="sm" className="h-8 gap-1.5 text-xs" onClick={openDialog} data-testid="ai-annual-plan-btn">
        <Sparkles className="h-3.5 w-3.5" /> Foreslå årsplan (AI)
      </Button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="ai-plan-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-lime" /> AI-styresekretær – forslag til årsplan {year}
            </DialogTitle>
            <DialogDescription>Basert på norske lovpålagte frister (skattemelding, generalforsamling, regnskap). Velg hva du vil legge til.</DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="grid place-items-center gap-3 py-12" data-testid="ai-plan-loading">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Styresekretæren lager et forslag …</p>
            </div>
          ) : plan ? (
            <div className="max-h-[55vh] space-y-5 overflow-y-auto pr-1">
              {plan.summary ? <p className="rounded-lg bg-muted/50 p-3 text-sm">{plan.summary}</p> : null}

              <div>
                <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Møter ({(plan.meetings || []).length})</h4>
                <div className="space-y-2">
                  {(plan.meetings || []).map((m, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setMeetSel((s) => ({ ...s, [i]: !s[i] }))}
                      data-testid={`ai-plan-meeting-${i}`}
                      className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${meetSel[i] ? "border-lime/50 bg-lime/5" : "border-border hover:bg-muted/50"}`}
                    >
                      <span className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border ${meetSel[i] ? "border-lime bg-lime text-lime-foreground" : "border-muted-foreground/40"}`}>{meetSel[i] ? <Check className="h-3 w-3" /> : null}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold">{m.title}</span>
                          <span className="rounded-full bg-muted px-2 py-0.5 text-[10px] font-medium text-muted-foreground">{typeLabel(m.type)}</span>
                          <span className="text-xs text-muted-foreground">{new Date(m.date).toLocaleDateString("nb-NO", { day: "2-digit", month: "short" })}</span>
                        </div>
                        {m.purpose ? <p className="mt-0.5 text-xs text-muted-foreground">{m.purpose}</p> : null}
                        {m.agenda?.length ? <p className="mt-1 text-[11px] text-muted-foreground">{m.agenda.length} saker: {m.agenda.map((a) => a.title).join(", ")}</p> : null}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <h4 className="mb-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">Årshjul-punkter ({(plan.wheel_points || []).length})</h4>
                <div className="space-y-2">
                  {(plan.wheel_points || []).map((p, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setWheelSel((s) => ({ ...s, [i]: !s[i] }))}
                      data-testid={`ai-plan-wheel-${i}`}
                      className={`flex w-full items-start gap-3 rounded-lg border p-3 text-left transition-colors ${wheelSel[i] ? "border-lime/50 bg-lime/5" : "border-border hover:bg-muted/50"}`}
                    >
                      <span className={`mt-0.5 grid h-4 w-4 shrink-0 place-items-center rounded border ${wheelSel[i] ? "border-lime bg-lime text-lime-foreground" : "border-muted-foreground/40"}`}>{wheelSel[i] ? <Check className="h-3 w-3" /> : null}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="rounded bg-blue-500/10 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-blue-700 dark:text-blue-300">{WHEEL_MONTHS[Math.max(1, Math.min(12, p.month)) - 1]}</span>
                          <span className="text-sm font-medium">{p.title}</span>
                        </div>
                        {p.rationale ? <p className="mt-0.5 text-xs text-muted-foreground">{p.rationale}</p> : null}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : null}

          <DialogFooter>
            {plan && !loading ? (
              <Button variant="ghost" className="mr-auto gap-1.5" onClick={generate} disabled={applying} data-testid="ai-plan-regenerate-btn">
                <RotateCw className="h-4 w-4" /> Nytt forslag
              </Button>
            ) : null}
            <Button variant="ghost" onClick={() => setOpen(false)}>Avbryt</Button>
            <Button onClick={apply} disabled={!plan || loading || applying} className="gap-1.5" data-testid="ai-plan-apply-btn">
              {applying ? <Loader2 className="h-4 w-4 animate-spin" /> : <CalendarPlus className="h-4 w-4" />} Legg til valgte
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
